import React, { useState, useEffect, useContext } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
// material
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  ListItem,
  Chip,
  Divider
} from '@mui/material';
import { red, green } from '@mui/material/colors';
// services
import { getEntry } from '../../services/Report';
// helper
import { getTimeZoneDate, getTimeZoneGTM } from '../../helpers/Util';
// components
import Page from '../../components/Page';
// contexts
import { useAuth } from '../../contexts/Auth';
import { SettingsContext } from '../../contexts/Settings';

const Show = () => {
  const { entryId } = useParams();
  const [searchParams] = useSearchParams();

  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);

  const [isLoading, setIsLoading] = useState(true);
  const [entry, setEntry] = useState({});
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const eventId = searchParams.get('eventId');
        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };

        const entry = await getEntry({ headers, eventId, entryId });
        setEntry(entry);
        setIsLoading(false);
      } catch (err) {
        setHasError(true);
        console.error(err);
      }
    };

    fetchData();
  }, [entryId, authUser, authSettings, searchParams]);

  if (hasError) {
    return (
      <Container>
        <Alert severity="error">
          An error occurred while trying to load the entry — Please, try to refresh the page!
        </Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  const path = `/entry/${entry._id}`;

  return (
    <Page title="Entry" path={path}>
      <Container>
        <ListItem>
          {entry.reasons.length === 0 ? (
            <Box sx={{ background: green[50], borderRadius: '20px', p: 2, flexGrow: 1 }}>
              <Box display="flex" alignItems="start" justifyContent="between">
                <Box mb={1}>
                  <Typography variant="span" color="text.secondary">
                    Entry time at {`(GTM${getTimeZoneGTM({ date: entry.booking.entry.time })})`}
                  </Typography>
                  <Typography component="div" variant="p">
                    <strong>
                      {`${getTimeZoneDate({ date: entry.booking.entry.time })
                        .tz('Europe/Lisbon')
                        .format('ddd, DD MMM, YYYY - HH:mm:ss')}`}
                    </strong>
                  </Typography>
                </Box>
              </Box>
              <Box mb={1}>
                <Typography variant="span" color="text.secondary">
                  Order
                </Typography>
                <Typography component="div" variant="p">
                  <strong>#{entry.order.number}</strong>
                </Typography>
              </Box>
              <Box mb={1}>
                <Typography variant="span" color="text.secondary">
                  Section
                </Typography>
                <Typography component="div" variant="p">
                  <strong>
                    {entry.booking.section.single ? ' - ' : entry.booking.section.name}
                  </strong>
                </Typography>
              </Box>
              <Box mb={1}>
                <Typography variant="span" color="text.secondary">
                  Ticket
                </Typography>
                <Typography component="div" variant="p">
                  <strong>{entry.booking.ticket.name}</strong>
                </Typography>
              </Box>
              <Box mb={1}>
                <Typography variant="span" color="text.secondary">
                  Gate
                </Typography>
                <Typography component="div" variant="p">
                  <strong>1</strong>
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                background: red[50],
                border: `8px solid ${red[500]}`,
                borderRadius: '20px',
                p: 2,
                flexGrow: 1
              }}
            >
              <Box display="flex" alignItems="start" justifyContent="between">
                <Box mb={1}>
                  <Typography variant="span" color="text.secondary">
                    Order
                  </Typography>
                  <Typography component="div" variant="p">
                    <strong>#{entry?.order?.number || ' - '}</strong>
                  </Typography>
                </Box>
              </Box>
              <Box mb={1}>
                <Typography variant="span" color="text.secondary">
                  Code
                </Typography>
                <Typography component="div" variant="p">
                  <strong>{entry?.booking?.qrcode || ' - '}</strong>
                </Typography>
              </Box>
              <Box mb={1}>
                <Typography variant="span" color="text.secondary">
                  Event
                </Typography>
                <Typography component="div" variant="p">
                  <strong>{entry?.order?.number ? entry.event.name : ' - '}</strong>
                </Typography>
              </Box>
              <Box mb={1}>
                <Typography variant="span" color="text.secondary">
                  Event Date {`(GTM${getTimeZoneGTM({ date: entry.event.date })})`}
                </Typography>
                <Typography component="div" variant="p">
                  <strong>
                    {entry?.order?.number
                      ? `${getTimeZoneDate({ date: entry.event.date })
                          .tz('Europe/Lisbon')
                          .format('ddd, DD MMM, YYYY - HH:mm:ss')}`
                      : ' - '}
                  </strong>
                </Typography>
              </Box>
              <Box mb={1}>
                <Typography variant="span" color="text.secondary">
                  Section
                </Typography>
                <Typography component="div" variant="p">
                  <strong>
                    {entry?.booking?.section?.single
                      ? ' - '
                      : entry?.booking?.section?.name || ' - '}
                  </strong>
                </Typography>
              </Box>
              <Box mb={1}>
                <Typography variant="span" color="text.secondary">
                  Ticket
                </Typography>
                <Typography component="div" variant="p">
                  <strong>{entry?.booking?.ticket?.name || ' - '}</strong>
                </Typography>
              </Box>
              <Box mb={1}>
                <Typography variant="span" color="text.secondary">
                  Status
                </Typography>
                <Typography component="div" variant="p">
                  <strong>{entry?.booking?.status || ' - '}</strong>
                </Typography>
              </Box>
              <Box mb={1}>
                <Typography variant="span" color="text.secondary">
                  Entry Between {`(GTM${getTimeZoneGTM({ date: entry.event.date })})`}
                </Typography>
                <Typography component="div" variant="p">
                  {entry?.booking?.entry ? (
                    <strong>
                      {`${getTimeZoneDate({ date: entry.booking.entry.starts })
                        .tz('Europe/Lisbon')
                        .format('ddd, DD MMM, YYYY - HH:mm:ss')}`}
                      {` (GTM${getTimeZoneGTM({ date: entry.booking.entry.starts })})`}
                      {' - '}
                      {`${getTimeZoneDate({ date: entry.booking.entry.ends })
                        .tz('Europe/Lisbon')
                        .format('ddd, DD MMM, YYYY - HH:mm:ss')}`}
                      {` (GTM${getTimeZoneGTM({ date: entry.booking.entry.ends })})`}
                    </strong>
                  ) : (
                    ' - '
                  )}
                </Typography>
              </Box>
              <Box mb={1}>
                <Typography variant="span" color="text.secondary">
                  Gate
                </Typography>
                <Typography component="div" variant="p">
                  <strong>1</strong>
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }}>
                <Chip label="Reasons" size="small" color="error" />
              </Divider>
              <Box mb={1}>
                {entry.reasons.map((reason, i) => (
                  <Typography key={i} component="div" variant="p" mb={1} color="error">
                    - {reason}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </ListItem>
      </Container>
    </Page>
  );
};

export default Show;
